/**
 * ClientPaymentCheckoutHeader — Tier 6B.4 checkout conversion block
 *
 * Sits at the top of the client-facing Payment tab and transforms the
 * payment step into a proper checkout moment with:
 *
 *   1. Gold "Secure Checkout" eyebrow + urgency-tuned headline
 *   2. Status badge (Pending Payment / Retainer Paid / Paid in Full)
 *   3. Amber urgency block ("your date is being held, not guaranteed")
 *   4. Large gold primary CTA — "Complete Payment — $X" — smooth-
 *      scrolls to the next due payment row in the existing timeline.
 *   5. Trust row (Secure payment · Encrypted checkout · Stripe-powered)
 *   6. Soft reassurance line ("contact us anytime")
 *
 * IMPORTANT: This component is PURE UI. It does not mutate payments,
 * call any payment API, or change any Stripe behaviour. The CTA only
 * scrolls the viewport to the next actionable row — the real payment
 * flow continues to live in ClientPaymentView below.
 */
import React from 'react';
import {
  CheckCircle2,
  Clock,
  Lock,
  Shield,
  CreditCard,
} from 'lucide-react';

const ClientPaymentCheckoutHeader = ({
  allPaid,
  totalPaid,
  totalDue,
  remaining,
  nextDue,
  nextPayment,
  status,
  fullPayment,
  fmt,
}) => {
  // EVERY status sentence here comes from `status` — one object, derived from
  // the booking's actual rows (lib/paymentStatus). This block used to decide
  // for itself: `totalPaid > 0` meant "Retainer Paid" and anything
  // outstanding meant the date was at risk, so a client whose retainer AND
  // contract balance were settled — with only a later extra charge left —
  // was told their date was "being held but not guaranteed".
  const BADGE_STYLE = {
    'Paid in Full': { color: '#22C55E', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)', icon: CheckCircle2 },
    'Contract Settled': { color: '#22C55E', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.26)', icon: CheckCircle2 },
    'Retainer Paid': { color: '#22C55E', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.26)', icon: CheckCircle2 },
    'Pending Payment': { color: '#E7C75E', bg: 'rgba(212,175,55,0.12)', border: 'rgba(212,175,55,0.32)', icon: Clock },
  };
  const badge = status?.badge
    ? { label: status.badge, ...BADGE_STYLE[status.badge] }
    : null;
  const BadgeIcon = badge?.icon;
  // No status means we could not read the booking's rows. Say nothing rather
  // than assert something that might be false.
  const headline = status?.headline;
  const subtext = status?.subtext;

  // No CTA handler here any more: this header states facts, and the controls
  // that take money live on the payment itself.

  return (
    <div
      className="rounded-[28px] p-6 sm:p-8"
      style={{
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
        border: '1px solid rgba(212,175,55,0.22)',
        boxShadow:
          '0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(212,175,55,0.05), 0 0 44px rgba(212,175,55,0.08)',
      }}
      data-testid="payment-checkout-header"
    >
      {/* Gold eyebrow */}
      <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]/75 font-medium">
        Secure Checkout
      </p>

      {/* Headline */}
      {headline && (
        <h2
          className="mt-2 text-xl sm:text-2xl font-semibold tracking-tight text-white leading-snug"
          data-testid="payment-checkout-headline"
        >
          {headline}
        </h2>
      )}

      {/* Subtext */}
      {subtext && (
        <p
          className="mt-2 text-[14px] sm:text-[15px] text-white/60 leading-relaxed max-w-xl"
          data-testid="payment-checkout-subtext"
        >
          {subtext}
        </p>
      )}

      {/* Status badge row */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {badge && (
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide"
            style={{
              background: badge.bg,
              border: `1px solid ${badge.border}`,
              color: badge.color,
            }}
            data-testid="payment-status-badge"
            data-status={badge.label.toLowerCase().replace(/\s+/g, '-')}
          >
            <BadgeIcon className="w-3.5 h-3.5" />
            {badge.label}
          </div>
        )}
        {/* THE HEADING AND THE FIGURE NOW COME FROM THE SAME OBJECT. This read
            as "Retainer Paid  $101.00 of $102.00 paid" — a retainer heading
            over a contract-plus-extras total. `status.progress` carries both
            the words and the numbers, so they cannot disagree again. */}
        {!allPaid && status?.progress?.due > 0 && (
          <span
            className="text-[11px] text-white/40 tabular-nums"
            data-testid="payment-checkout-progress"
          >
            {status.progress.label}: {fmt(status.progress.paid)} of {fmt(status.progress.due)}
          </span>
        )}
      </div>

      {/* Urgency block — ONLY when the date genuinely is not secured, i.e.
          the retainer itself is outstanding. `status.urgency` is null in every
          other state, so a settled retainer can never be told its date is
          "being held but not guaranteed". */}
      {!allPaid && status?.urgency && (
        <div
          className="mt-5 rounded-xl px-4 py-3 flex items-start gap-3"
          style={{
            background: 'rgba(245,158,11,0.06)',
            border: '1px solid rgba(245,158,11,0.2)',
          }}
          data-testid="payment-urgency-block"
        >
          <Clock className="w-4 h-4 text-amber-300/80 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[13px] text-amber-100/90 font-medium leading-snug">
              {status.urgency.title}
            </p>
            <p className="text-[11px] text-amber-100/50 mt-0.5 leading-relaxed">
              {status.urgency.body}
            </p>
          </div>
        </div>
      )}

      {/* THE LINE THAT REPLACED THE PAY BUTTON. A full-width control labelled
          "Pay <label> — $amount" lived here and did not take a payment: it
          scrolled the page and landed the client on Zelle whatever they
          wanted, showing the net amount while a card would charge the gross.
          A button that says Pay must take money. This states the facts and
          the real controls sit on the payment itself, both prices visible. */}
      {!allPaid && nextDue && (() => {
        // BLOCK S29 — one due state (upcoming / due / overdue) from the server.
        const np = nextPayment || null;
        const state = np?.state || 'due';
        const label = fullPayment ? 'Full payment' : (np?.label || nextDue.label || (nextDue.type === 'retainer' ? 'Retainer' : 'Balance'));
        const amt = fmt(Number(np?.amount ?? nextDue.amount) || 0);
        // Parse yyyy-mm-dd PARTS — never new Date(iso), which shifts a day.
        const MON = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        const parseDue = (iso) => {
          if (!iso || typeof iso !== 'string') return { full: '', short: '' };
          const [y, m, d] = iso.split('-').map(Number);
          if (!y || !m || !d || m < 1 || m > 12) return { full: '', short: '' };
          return { full: `${MON[m - 1]} ${d}, ${y}`, short: `${MON[m - 1]} ${d}` };
        };
        const { full: dFull, short: dShort } = parseDue(np?.dueDate);
        return (
          <div
            className="mt-6 text-center"
            data-testid="client-next-payment-state"
            data-state={state}
          >
            {state === 'upcoming' ? (
              <p className="text-[13px] sm:text-[14px] text-white/70" style={{ minHeight: 44 }} data-testid="payment-due-line">
                Next payment: <span className="text-white/90 font-semibold">{amt}</span>{dFull ? <> · {dFull}</> : null}
              </p>
            ) : state === 'overdue' ? (
              <div className="flex flex-col items-center gap-2" style={{ minHeight: 44 }}>
                <p className="text-[13px] sm:text-[14px] text-white/70" data-testid="payment-due-line">
                  Past due: {label} — <span className="text-white/90 font-semibold">{amt}</span>
                </p>
                {dShort ? (
                  <span
                    className="inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold"
                    style={{ color: '#F87171', background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.32)' }}
                  >
                    Overdue since {dShort}
                  </span>
                ) : null}
              </div>
            ) : (
              <div data-testid="payment-due-line">
                <p
                  className="text-[26px] sm:text-[30px] leading-tight text-[#F1D27A]"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
                >
                  {label} due now — {amt}
                </p>
                {remaining > 0 ? (
                  <p className="mt-2 text-[12px] text-white/40 tabular-nums">
                    Total outstanding: {fmt(remaining)}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        );
      })()}

      {/* Trust row */}
      {!allPaid && (
        <div
          className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-white/45"
          data-testid="payment-trust-row"
        >
          <span className="inline-flex items-center gap-1.5">
            <Lock
              className="w-3.5 h-3.5 text-[#D4AF37]/60"
              strokeWidth={2.25}
            />
            Secure payment
          </span>
          <span aria-hidden className="text-white/15">
            ·
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Shield
              className="w-3.5 h-3.5 text-[#D4AF37]/60"
              strokeWidth={2.25}
            />
            Encrypted checkout
          </span>
          <span aria-hidden className="text-white/15">
            ·
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CreditCard
              className="w-3.5 h-3.5 text-[#D4AF37]/60"
              strokeWidth={2.25}
            />
            Stripe-powered
          </span>
        </div>
      )}

      {/* Reassurance — always visible */}
      <p
        className="mt-4 text-center text-[11px] text-white/30 leading-relaxed"
        data-testid="payment-reassurance"
      >
        If you have any questions, contact us anytime — we're here to help.
      </p>
    </div>
  );
};

export default ClientPaymentCheckoutHeader;
export { ClientPaymentCheckoutHeader };
